import React, {useEffect, useState} from "react";
import CountriesSelector from "../../../contriesSelector/CountriesSelector";
import {RefreshIcon} from "../../../../assets/icons/RefreshIcon";
import {getTwilioPhoneNumbers} from "../../../../api/getTwilioPhoneNumbers";
import useUserContext from "../../../../hooks/useUserContext";
import useAppContext from "../../../../hooks/useAppContext";
import Loader from "../../../loader/Loader";
import {formatPhoneNumber} from "../../../../utils/friendlyPhone";
import useToast from "../../../../hooks/useToast";

export interface PhoneNumberObj {
    phoneNumber?: string, price?: number, priceToShow?: number, isoCountry?: string, token?: string
}

export const EMPTY_PHONE_NUMBER_OBJ: PhoneNumberObj = { phoneNumber: undefined, price: undefined, isoCountry: 'US', token: undefined };

export const TwilioPhoneCatalogPicker = ({ label, onNumberChange, disabled = false }: { label?: string, onNumberChange: (payload: PhoneNumberObj) => void, disabled?: boolean }) => {
    const { selectedDomain } = useUserContext();
    const { data: { shop }} = useAppContext();
    const pricingPlan = shop?.data?.pricingPlan;
    const includedPhoneNumberIso = pricingPlan?.includedPhoneNumberIso || undefined;

    const { showToast } = useToast();
    const [selectedPhoneNumberObj, setSelectedPhoneNumberObj] = useState<PhoneNumberObj>(EMPTY_PHONE_NUMBER_OBJ);
    const [ isLoadingCountryPhoneNumbers, setIsLoadingCountryPhoneNumbers ] = useState(false);
    const [phoneNumberSelection, setPhoneNumberSelection] = useState<any[]>([]);
    const isRefreshButtonEnabled = !isLoadingCountryPhoneNumbers && phoneNumberSelection?.length > 1 && selectedPhoneNumberObj?.phoneNumber;

    const setRandomPhoneNumber = (phoneNumbers: any[], selectedCountryIso: string) => {
        const randomPhoneNumberFromSelection = phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)];
        setSelectedPhoneNumberObj({
            phoneNumber: randomPhoneNumberFromSelection.phone_number,
            price: randomPhoneNumberFromSelection.price,
            isoCountry: selectedCountryIso,
            token: randomPhoneNumberFromSelection.token,
        });
    }

    const onCountryChange = async (newCountryCode: string) => {
        try {
            const countryISO2 = includedPhoneNumberIso ? [includedPhoneNumberIso] : newCountryCode.split(' ');
            if(countryISO2[0]) {
                setIsLoadingCountryPhoneNumbers(true);
                setSelectedPhoneNumberObj({...EMPTY_PHONE_NUMBER_OBJ, isoCountry: countryISO2[0]});
                const phoneNumberResponse = selectedDomain ? await getTwilioPhoneNumbers(selectedDomain, countryISO2[0]) : null;
                if (phoneNumberResponse?.data?.length) {
                    setPhoneNumberSelection(phoneNumberResponse.data);
                    setRandomPhoneNumber(phoneNumberResponse.data, countryISO2[0]);
                } else {
                    setSelectedPhoneNumberObj({...EMPTY_PHONE_NUMBER_OBJ, isoCountry: countryISO2[0]});
                }
            } else {
                setSelectedPhoneNumberObj(EMPTY_PHONE_NUMBER_OBJ);
            }
            setIsLoadingCountryPhoneNumbers(false);
        } catch(error) {
            setSelectedPhoneNumberObj(EMPTY_PHONE_NUMBER_OBJ);
        }
    }

    const getPhoneNumberView = () => {
        if(isLoadingCountryPhoneNumbers) return <Loader size={48} />;
        if(!selectedPhoneNumberObj?.phoneNumber) return "No numbers available";
        return formatPhoneNumber(selectedPhoneNumberObj?.phoneNumber);
    }

    useEffect(() => {
        setSelectedPhoneNumberObj({ ...EMPTY_PHONE_NUMBER_OBJ, isoCountry: includedPhoneNumberIso || selectedPhoneNumberObj.isoCountry });
        onCountryChange(`${includedPhoneNumberIso || selectedPhoneNumberObj.isoCountry} XX`);
    }, [includedPhoneNumberIso]);

    useEffect(() => {
        onNumberChange(selectedPhoneNumberObj);
    }, [selectedPhoneNumberObj]);

    return (
        <div className="phone-number-catalog-picker">
            {includedPhoneNumberIso ? null :
                <div className="country-selector">
                    <CountriesSelector selectedCountry={selectedPhoneNumberObj?.isoCountry || 'US'} setSelectedCountry={onCountryChange} valueProp="country" isDisabled={disabled || includedPhoneNumberIso}/>
                </div>
            }
            <div className="phone-field-container">
                {label ? <div className="description">{label}</div> : null}
                <div className={`phone-field ${disabled ? 'disabled' : ''}`}>
                    {getPhoneNumberView()}
                    {isRefreshButtonEnabled && !disabled ? <div className="refresh-number" onClick={() => selectedPhoneNumberObj.isoCountry && setRandomPhoneNumber(phoneNumberSelection, selectedPhoneNumberObj.isoCountry)}><RefreshIcon width={24} color="#1575fb" /></div> : <div className="refresh-number disabled"><RefreshIcon width={24} color="#e1e1e1" /></div>}
                </div>
            </div>
        </div>
    )
}